// Shopify-Iubenda Integration
(function() {
  const DEBUG = true; // Set to true to enable detailed logging
  
  function log(message, data) {
    if (DEBUG) {
      if (data !== undefined) {
        console.log(`[Shopify-Iubenda] ${message}`, data);
      } else {
        console.log(`[Shopify-Iubenda] ${message}`);
      }
    }
  }
  
  log('Initializing Shopify-Iubenda integration');

  // Helper function to safely access window objects
  function safeGet(obj, path) {
    return path.split('.').reduce((acc, part) => acc && acc[part] ? acc[part] : undefined, obj);
  }

  // Track if we've already set consent to avoid redundant updates
  let consentSet = false;
  
  // Map GTM consent types to Shopify consent types
  const consentMap = {
    'ad_storage': 'marketing',
    'analytics_storage': 'analytics',
    'functionality_storage': 'preferences',
    'personalization_storage': 'sale_of_data',
    'security_storage': 'necessary'
  };

  // Setup Shopify consent handler
  function setupShopifyConsentHandler() {
    log('Setting up Shopify consent handler');
    
    // Check if Shopify object exists
    if (typeof window.Shopify === 'undefined') {
      window.Shopify = {};
    }
    
    // Modern approach using granular consent API if available
    checkAndSetShopifyConsent();
    
    // Listen for when Customer Events API is loaded
    document.addEventListener('Shopify:CustomerPrivacyLoaded', function() {
      log('Customer Privacy API loaded event detected');
      checkAndSetShopifyConsent();
    });
  }
  
  // Check and set Shopify consent using the appropriate method
  function checkAndSetShopifyConsent() {
    // Try to detect the appropriate consent API
    if (window.Shopify && window.Shopify.customerPrivacy) {
      log('Shopify consent API loaded');
      setShopifyConsent();
    } else {
      // If not immediately available, set up a fallback check
      setTimeout(function() {
        if (window.Shopify && window.Shopify.customerPrivacy) {
          log('Shopify consent API found on delayed check');
          setShopifyConsent();
        } else {
          log('Warning: Shopify consent API not available');
        }
      }, 1000);
    }
  }

  // Set Shopify consent
  function setShopifyConsent() {
    if (consentSet) {
      log('Skipping redundant consent update');
      return;
    }

    log('Setting Shopify consent with analytics enabled');
    
    try {
      // Try to use the preferred granular consent API
      if (window.Shopify && window.Shopify.customerPrivacy && typeof window.Shopify.customerPrivacy.setTrackingConsent === 'function') {
        // Enable analytics tracking in Shopify
        window.Shopify.customerPrivacy.setTrackingConsent(true);
        log('Set Shopify tracking consent using binary API');
      } else if (window.Shopify && window.Shopify.Analytics && window.Shopify.Analytics.GDPRCookies) {
        // Alternative method for some older Shopify stores
        window.Shopify.Analytics.GDPRCookies.setTrackingConsent(true, new Date());
        log('Set Shopify tracking consent using legacy Analytics API');
      } else {
        log('Warning: No Shopify consent API methods found');
      }
    } catch (e) {
      log('Error setting Shopify consent', e);
    }
    
    // Get current consent state from GTM
    const currentConsent = getCurrentConsentState();
    
    // Update the global dataLayer consent
    updateGTMConsent(currentConsent);
    
    consentSet = true;
  }

  // Get current consent state from GTM
  function getCurrentConsentState() {
    // Default consent state
    const defaultConsent = {
      analytics: true,
      marketing: false,
      preferences: false,
      sale_of_data: false,
      necessary: true
    };
    
    // Try to get current consent from dataLayer
    log('Searching dataLayer for current consent state...');
    
    // Try to detect Iubenda consent directly if available
    try {
      if (window._iub && window._iub.cs && window._iub.cs.api && typeof window._iub.cs.api.getConsent === 'function') {
        const iubendaConsent = window._iub.cs.api.getConsent();
        if (iubendaConsent && iubendaConsent.purposes) {
          log('Found direct Iubenda consent data:', iubendaConsent);
          
          // Extract purposes from Iubenda
          const extractedConsent = { ...defaultConsent };
          
          // Map Iubenda purpose IDs to our consent types (these may need adjustment based on your configuration)
          // Common mappings, but check your Iubenda setup for exact values
          const purposesMapping = {
            1: 'necessary',        // Essential/necessary cookies
            2: 'preferences',      // Preferences/functionality
            3: 'analytics',        // Analytics/statistics
            4: 'marketing',        // Marketing/advertising
            5: 'sale_of_data'      // Sale of personal data
          };
          
          // Extract consent values from Iubenda
          for (const [purposeId, value] of Object.entries(iubendaConsent.purposes)) {
            const purposeKey = purposesMapping[purposeId];
            if (purposeKey) {
              extractedConsent[purposeKey] = !!value;
            }
          }
          
          // Always force analytics to true
          extractedConsent.analytics = true;
          return extractedConsent;
        }
      }
    } catch (e) {
      log('Error accessing Iubenda consent directly:', e);
    }
    
    // Fallback to dataLayer if direct Iubenda access fails
    if (window.dataLayer) {
      for (let i = window.dataLayer.length - 1; i >= 0; i--) {
        const item = window.dataLayer[i];
        if (item && item.consent_state) {
          log('Found consent state in dataLayer:', item.consent_state);
          
          // Extract consent values from dataLayer
          const extractedConsent = { ...defaultConsent };
          
          // Map GTM consent types to our internal format
          for (const [gtmType, internalType] of Object.entries(consentMap)) {
            if (item.consent_state[gtmType] !== undefined) {
              extractedConsent[internalType] = item.consent_state[gtmType] === 'granted';
            }
          }
          
          // Always force analytics to true
          extractedConsent.analytics = true;
          
          return extractedConsent;
        }
      }
    }
    
    // Try to read consent from cookies directly as a last resort
    try {
      const cookieConsent = document.cookie.split(';').find(c => c.trim().startsWith('euconsent=') || c.trim().startsWith('_iub_cs-'));
      if (cookieConsent) {
        log('Found consent cookie, assuming user has provided consent');
        return {
          analytics: true,
          marketing: true,
          preferences: true,
          sale_of_data: true,
          necessary: true
        };
      }
    } catch (e) {
      log('Error checking consent cookies:', e);
    }
    
    log('No consent state found in dataLayer or cookies, using default');
    return defaultConsent;
  }

  // Update GTM consent while preserving existing values
  function updateGTMConsent(consentState) {
    // Always force analytics to true
    const finalConsent = {
      ...consentState,
      analytics: true
    };
    
    log('Consent updated with analytics enabled:', finalConsent);
    
    // Map internal consent categories to GTM consent types
    if (typeof window.gtag === 'function') {
      const gtagConsent = {};
      
      // Map our internal format back to GTM consent types
      for (const [gtmType, internalType] of Object.entries(consentMap)) {
        gtagConsent[gtmType] = finalConsent[internalType] ? 'granted' : 'denied';
      }
      
      // Always force analytics to granted
      gtagConsent.analytics_storage = 'granted';
      
      log('Updating GTM consent with:', gtagConsent);
      window.gtag('consent', 'update', gtagConsent);
    } else {
      log('gtag function not available');
    }
  }

  // Set up minimal consent event listeners
  function setupConsentEventListeners() {
    log('Setting up minimal consent event listeners');
    
    try {
      // Listen for Iubenda consent events
      window.addEventListener('message', function(event) {
        if (event.data && event.data.event && event.data.event.startsWith('iubenda_consent_given')) {
          log('Banner button clicked, updating consent once');
          log('Iubenda event data:', event.data);
          
          // Allow updating consent again after user interaction
          consentSet = false;
          
          // Use a longer delay to ensure Iubenda has time to update all consent values
          // Check and update consent multiple times with increasing delays
          setTimeout(checkAndSetShopifyConsent, 100);  // First check quickly
          setTimeout(checkAndSetShopifyConsent, 500);  // Second check after 500ms
          setTimeout(checkAndSetShopifyConsent, 1000); // Final check after 1 second
        }
      });
    } catch (e) {
      log('Error setting up message event listener', e);
    }
    
    // Also try to detect clicks on the banner directly as a fallback
    try {
      document.addEventListener('click', function(e) {
        if (e.target && (
            e.target.id === 'iubenda-cs-accept-btn' || 
            e.target.id === 'iubenda-cs-btn' ||
            (e.target.className && e.target.className.includes('iubenda'))
        )) {
          log('Potential cookie banner interaction detected');
          // Reset consent flag
          consentSet = false;
          
          // Set up staggered checks to ensure we catch the updated consent values
          setTimeout(function() {
            checkAndSetShopifyConsent();
          }, 500);
          
          setTimeout(function() {
            checkAndSetShopifyConsent();
          }, 1000);
          
          setTimeout(function() {
            checkAndSetShopifyConsent();
          }, 2000);
        }
      });
    } catch (e) {
      log('Error setting up click event listener', e);
    }
  }

  // Initialize with a small delay to ensure page is loaded
  setTimeout(function() {
    setupShopifyConsentHandler();
    setupConsentEventListeners();
    
    // Process consent once on page load with a delay to ensure all scripts are loaded
    setTimeout(function() {
      checkAndSetShopifyConsent();
    }, 500);
  }, 100);
})();